import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import getBrowserHistory from 'yoda-core-components/lib/navigation/history/getBrowserHistory';
import classNames from 'classnames/bind';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import ModalBox from 'yoda-core-components/lib/deprecated/ModalBox/ModalBox';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import ChatLine from '../../../../assets/svg/chat.svg';
import SuccessThick from '../../../../assets/svg/success-thick.svg';
import XIcon from '../../../../assets/svg/x.svg';
import actions from '../../../../actions/OrderAction';
import analyticsActions from '../../../../actions/AnalyticsAction';
import Link from '../../../Link/Link';
import footerConfig from '../../config';
import styles from './index.css';
import { selectDeviceType, selectFeatureFlags } from '../../../../selectors/ContextSelector';

const cx = classNames.bind(styles);

const {
    Validations: {
        emailRegex,
        phoneRegex,
        formatPhoneRegex,
        errorMessage,
        invalidEmailMessage,
        invalidPhoneMessage,
        emptyEmailMessage,
        emptyPhoneMessage,
    },
    EmailSignUp,
    SmsSignUp,
} = footerConfig;

const TAB_EMAIL = 'email';
const TAB_SMS = 'sms';

export const YodaSubscription = ({
    deviceType = {},
    expandedFooter = false,
    offerMessageData = {},
    subscriptionStatus = {},
    featureFlags = {},
    actions: dispatchActions,
}) => {
    const [activeTab, setActiveTab] = useState(TAB_EMAIL);
    const [inputValue, setInputValue] = useState('');
    const [showInputError, setShowInputError] = useState(false);
    const [inputErrorText, setInputErrorText] = useState('');
    const [isPromotionSignUpClicked, setIsPromotionSignUpClicked] = useState(false);
    const [emailSuccess, setEmailSuccess] = useState(false);
    const [smsSuccess, setSmsSuccess] = useState(false);
    const [isOfferMessageModalOpen, setIsOfferMessageModalOpen] = useState(false);
    const unmountRef = useRef(null);

    useEffect(() => {
        unmountRef.current = getBrowserHistory().listen(() => {
            setShowInputError(false);
            setInputErrorText('');
        });
        return () => {
            if (unmountRef.current) unmountRef.current();
        };
    }, []);

    useEffect(() => {
        if (subscriptionStatus.from !== 'footer') return;

        if (subscriptionStatus.isSuccess) {
            setShowInputError(false);
            setInputErrorText('');
            setInputValue('');

            const isEmail = subscriptionStatus.type === 'EMAIL';
            const optinType = isEmail ? 'email' : 'sms';
            dispatchActions.subscriptionSuccessAnalytics({ optinType, source: 'page footer' });

            if (isEmail) {
                setEmailSuccess(true);
            } else {
                setSmsSuccess(true);
            }
        } else {
            setShowInputError(true);
            setInputErrorText(errorMessage);
            dispatchActions.triggerFormError([{ errorDescription: errorMessage }]);
        }
        setIsPromotionSignUpClicked(false);
    }, [subscriptionStatus]);

    const handleTabSwitch = (tab) => {
        if (tab === activeTab) return;
        setActiveTab(tab);
        setInputValue('');
        setShowInputError(false);
        setInputErrorText('');
        dispatchActions.subscriptionSuccessAnalytics({
            optinType: tab === TAB_EMAIL ? 'email' : 'sms',
            source: 'page footer tab switch',
        });
    };

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
        if (showInputError) {
            setShowInputError(false);
            setInputErrorText('');
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const val = inputValue.trim();

        if (activeTab === TAB_EMAIL) {
            if (!val) {
                setShowInputError(true);
                setInputErrorText(emptyEmailMessage);
                dispatchActions.triggerFormError([{ errorDescription: emptyEmailMessage }]);
                return;
            }
            if (!emailRegex.test(val)) {
                setShowInputError(true);
                setInputErrorText(invalidEmailMessage);
                dispatchActions.triggerFormError([{ errorDescription: invalidEmailMessage }]);
                return;
            }
            setIsPromotionSignUpClicked(true);
            dispatchActions.optin({ type: 'EMAIL', value: val });
        } else {
            const formattedPhone = val.replace(formatPhoneRegex, '$1-$2-$3');
            if (!val) {
                setShowInputError(true);
                setInputErrorText(emptyPhoneMessage);
                dispatchActions.triggerFormError([{ errorDescription: emptyPhoneMessage }]);
                return;
            }
            if (!phoneRegex.test(formattedPhone)) {
                setShowInputError(true);
                setInputErrorText(invalidPhoneMessage);
                dispatchActions.triggerFormError([{ errorDescription: invalidPhoneMessage }]);
                return;
            }
            setIsPromotionSignUpClicked(true);
            dispatchActions.optin({ type: 'MOBILE', value: val });
        }
    };

    const {
        OfferMessage: { title = '', Description = '' } = {},
        TermsLegal = [],
        Privacy = [],
        SubscriptionLabels: { signUpMessage = '', done = '' } = {},
    } = offerMessageData;

    const { enableIrisFooterApi = true } = featureFlags;
    const { isDesktop = true } = deviceType;

    const linkUrlPrivacy =
        isDesktop || enableIrisFooterApi ? Privacy[0]?.linkPath : Privacy[0]?.linkPathMobile;

    // Styling
    const subscriptionWrapperStyle = dt([
        'lg:pt-0',
        'lg:mb-2',
        'lg:pr-9',
        'lg:pl-8',
        'lg:mt-6',
        'lg:max-w-[684px]',
        'lg:w-[33.2%]',
        'lg:inline-block',
        'lg:align-top',
        'md:block',
        'md:mx-auto',
        'md:w-fit',
        'md:py-6',
        'sm:my-0',
        'sm:py-4',
        'sm:pb-0',
        'sm:w-auto',
        'sm:px-6',
        'sm:block',
        'sm:!mx-auto',
    ]);
    const subsTitleStyle = dt([
        'font-semibold',
        'text-[14px]',
        'leading-5',
        'text-center',
        'text-[#202020]',
        'pb-4',
        'not-italic',
        "[font-feature-settings:'liga'_off,'clig'_off]",
    ]);
    const iconBlockStyle = dt(['text-center', 'mb-2', 'flex', 'justify-center']);
    const errorBlockStyle = dt([
        'text-error-border',
        'text-small',
        'lg:w-[20.438rem]',
        'md:w-[20.438rem]',
        'sm:w-[20.438rem]',
        'py-[5px]',
        'my-0',
        'mx-auto',
    ]);
    const errorBorderStyle = dt(['border-2', 'border-solid', 'border-error-border']);
    const inputStyle = dt([
        'py-1',
        'px-4',
        'w-full',
        'box-border',
        'h-12',
        'border',
        'border-solid',
        'border-gray-60',
        'text-small',
        'focus:outline-none',
        'rounded-l',
        'bg-white',
        'italic',
    ]);
    const submitBtnStyle = dt([
        'h-12',
        'px-4',
        'box-border',
        'rounded-r',
        'border',
        '!border-solid',
        '!border-brand-red',
        'bg-brand-red',
        'text-white',
        'text-small',
        'cursor-pointer',
        'whitespace-nowrap',
        'disabled:opacity-50',
        'disabled:cursor-not-allowed',
    ]);
    const inputRowStyle = dt(['flex', 'items-stretch', 'w-full', 'sm:max-w-[327px]', 'mx-auto']);
    const inputWrapperStyle = dt(['relative', 'flex-1']);
    const inputLoaderStyle = dt([
        'absolute',
        'right-3',
        'top-1/2',
        '-translate-y-1/2',
        'w-4',
        'h-4',
        'rounded-full',
        'border-2',
        'border-solid',
        'border-gray-15',
        'border-t-gray-60',
        'animate-loaderRotate',
    ]);
    const legalStyle = dt([
        'font-normal',
        'text-xsmall',
        'text-center',
        'mt-2',
        'text-gray-70',
        'tracking-normal',
        'sm:max-w-[327px]',
        'mx-auto',
    ]);
    const anchorStyle = dt(['text-gray-70', 'underline']);
    const successStyle = dt(['text-small', 'text-center', 'text-black', 'mt-2', 'px-2']);
    const tabBarStyle = dt(['flex', 'mb-0']);
    const tabSeparatorStyle = dt([
        'flex',
        'h-px',
        'justify-center',
        'items-center',
        'self-stretch',
        'bg-[#D8D8D8]',
        'mb-4',
    ]);
    const activeTabStyle = dt([
        'flex-1',
        'text-center',
        'text-brand-red',
        'font-bold',
        'text-[14px]',
        'leading-5',
        'not-italic',
        "[font-feature-settings:'liga'_off,'clig'_off]",
    ]);
    const activeTabSpanStyle = dt([
        'inline-block',
        'border-b-4',
        'border-solid',
        'border-brand-red',
        'px-4',
        'py-2',
    ]);
    const inactiveTabSpanStyle = dt(['inline-block', 'px-4', 'py-2']);
    const inactiveTabStyle = dt([
        'flex-1',
        'text-small',
        'text-center',
        'text-gray-70',
        'cursor-pointer',
        'font-semibold',
    ]);

    const isEmailTab = activeTab === TAB_EMAIL;
    const currentSuccess = isEmailTab ? emailSuccess : smsSuccess;
    const smsSuccessMessage = emailSuccess
        ? SmsSignUp.successMessageBoth
        : SmsSignUp.successMessage;

    const successHeading = isEmailTab ? 'Thanks for signing up!' : 'Thank you for signing up!';
    const successBody = isEmailTab
        ? EmailSignUp.successMessage.replace('Thanks for signing up! ', '')
        : smsSuccessMessage.replace('Thank you for signing up! ', '');

    if (!expandedFooter) return null;

    return (
        <>
            <div
                data-automation-id="subscription-wrapper"
                className={`${styles.subscriptionWrapper} ${subscriptionWrapperStyle}`}
            >
                <div data-automation-id="footer-block-subscribe" className={styles.containerWidth}>
                    <div className={cx('iconBlock', iconBlockStyle)}>
                        <Icon
                            iconType="svg"
                            classNames="icon"
                            viewBox="0 0 40 40"
                            width="40px"
                            height="40px"
                            automationId="footer-error-icon"
                            pathClassName={styles.chatLine}
                        >
                            <ChatLine />
                        </Icon>
                    </div>
                    <div
                        data-automation-id="promo-title"
                        className={`${styles.subsTitle} ${subsTitleStyle}`}
                        style={{ fontFamily: '"Open Sans", sans-serif' }}
                    >
                        {signUpMessage}
                    </div>

                    {/* Tab switcher */}
                    <div className={tabBarStyle} role="tablist">
                        <button
                            role="tab"
                            aria-selected={isEmailTab}
                            data-automation-id="email-tab"
                            className={isEmailTab ? activeTabStyle : inactiveTabStyle}
                            style={
                                isEmailTab ? { fontFamily: '"Open Sans", sans-serif' } : undefined
                            }
                            onClick={() => handleTabSwitch(TAB_EMAIL)}
                            type="button"
                        >
                            {isEmailTab ? (
                                <span className={activeTabSpanStyle}>{EmailSignUp.tabLabel}</span>
                            ) : (
                                <span className={inactiveTabSpanStyle}>{EmailSignUp.tabLabel}</span>
                            )}
                        </button>
                        <button
                            role="tab"
                            aria-selected={!isEmailTab}
                            data-automation-id="sms-tab"
                            className={!isEmailTab ? activeTabStyle : inactiveTabStyle}
                            style={
                                !isEmailTab ? { fontFamily: '"Open Sans", sans-serif' } : undefined
                            }
                            onClick={() => handleTabSwitch(TAB_SMS)}
                            type="button"
                        >
                            {!isEmailTab ? (
                                <span className={activeTabSpanStyle}>{SmsSignUp.tabLabel}</span>
                            ) : (
                                <span className={inactiveTabSpanStyle}>{SmsSignUp.tabLabel}</span>
                            )}
                        </button>
                    </div>
                    <div className={tabSeparatorStyle} />

                    <form onSubmit={handleSubmit} noValidate>
                        <div className={inputRowStyle}>
                            <div className={inputWrapperStyle}>
                                <input
                                    data-automation-id="subscription-input"
                                    data-tl-private="true"
                                    data-qm-block="true"
                                    type={isEmailTab ? 'email' : 'tel'}
                                    placeholder={isEmailTab ? EmailSignUp.placeholder : SmsSignUp.placeholder}
                                    aria-required="true"
                                    aria-label={isEmailTab ? EmailSignUp.placeholder : SmsSignUp.placeholder}
                                    value={inputValue}
                                    onChange={handleInputChange}
                                    className={`${inputStyle} ${showInputError ? errorBorderStyle : ''} ${(inputValue || isPromotionSignUpClicked) ? 'pr-8' : ''}`}
                                />
                                {inputValue && !isPromotionSignUpClicked && (
                                    <button
                                        type="button"
                                        data-automation-id="subscription-clear"
                                        aria-label="Clear input"
                                        onClick={() => { setInputValue(''); setShowInputError(false); setInputErrorText(''); }}
                                        className={dt(['absolute', 'right-3', 'top-1/2', '-translate-y-1/2', 'bg-transparent', 'border-0', 'p-0', 'cursor-pointer', 'flex', 'items-center'])}
                                    >
                                        <Icon
                                            iconType="svg"
                                            classNames="icon"
                                            viewBox="0 0 24 24"
                                            width="19.2px"
                                            height="19.2px"
                                            automationId="subscription-clear-icon"
                                        >
                                            <XIcon />
                                        </Icon>
                                    </button>
                                )}
                                {isPromotionSignUpClicked && (
                                    <span
                                        data-automation-id="subscription-loader"
                                        className={inputLoaderStyle}
                                        aria-hidden="true"
                                    />
                                )}
                            </div>
                            <button
                                type="submit"
                                data-automation-id="subscription-submit"
                                className={submitBtnStyle}
                                disabled={isPromotionSignUpClicked}
                                aria-busy={isPromotionSignUpClicked}
                            >
                                {isEmailTab ? EmailSignUp.signUpButton : SmsSignUp.signUpButton}
                            </button>
                        </div>

                        {showInputError && (
                            <div
                                className={errorBlockStyle}
                                data-automation-id="subscription-error"
                            >
                                <span className={styles.errorTextBlock}>{inputErrorText}</span>
                            </div>
                        )}

                        {currentSuccess ? (
                            <div
                                data-automation-id="subscription-success-message"
                                className={dt(['mt-2', 'sm:max-w-[327px]', 'mx-auto'])}
                            >
                                <div
                                    className={dt([
                                        'flex',
                                        'items-center',
                                        'justify-center',
                                        'gap-1',
                                    ])}
                                >
                                    <Icon
                                        iconType="svg"
                                        classNames="icon"
                                        viewBox="0 0 32 32"
                                        width="16px"
                                        height="16px"
                                        automationId="subscription-success-icon"
                                    >
                                        <SuccessThick />
                                    </Icon>
                                    <span
                                        style={{
                                            color: 'var(--Grayscale-Black, #202020)',
                                            fontFeatureSettings: "'liga' off, 'clig' off",
                                            fontFamily: '"Open Sans"',
                                            fontSize: '14px',
                                            fontStyle: 'normal',
                                            fontWeight: 600,
                                            lineHeight: '20px',
                                        }}
                                    >
                                        {successHeading}
                                    </span>
                                </div>
                                <p
                                    style={{
                                        color: 'var(--Grayscale-Black, #202020)',
                                        textAlign: 'center',
                                        fontFeatureSettings: "'liga' off, 'clig' off",
                                        fontFamily: '"Open Sans"',
                                        fontSize: '14px',
                                        fontStyle: 'normal',
                                        fontWeight: 400,
                                        lineHeight: '20px',
                                        letterSpacing: '-0.14px',
                                    }}
                                >
                                    {successBody}
                                </p>
                            </div>
                        ) : isEmailTab ? (
                            <p className={legalStyle} data-automation-id="email-legal">
                                {EmailSignUp.legalText}&nbsp;
                                <Link className={anchorStyle} href={EmailSignUp.termsLink}>
                                    {EmailSignUp.termsLabel}
                                </Link>
                                {' and '}
                                <Link className={anchorStyle} href={EmailSignUp.privacyLink}>
                                    {EmailSignUp.privacyLabel}
                                </Link>
                                .
                            </p>
                        ) : (
                            <p className={legalStyle} data-automation-id="sms-legal">
                                {SmsSignUp.legalText}&nbsp;
                                <Link className={anchorStyle} href={SmsSignUp.privacyLink}>
                                    {SmsSignUp.privacyLabel}
                                </Link>
                                {SmsSignUp.legalTextSuffix}&nbsp;
                                <Link className={anchorStyle} href={SmsSignUp.termsLink}>
                                    {SmsSignUp.termsLabel}
                                </Link>
                                {' & acknowledge the '}
                                <Link className={anchorStyle} href={SmsSignUp.privacyLink}>
                                    {SmsSignUp.privacyLabel}
                                </Link>
                                .
                            </p>
                        )}
                    </form>
                </div>
            </div>

            <ModalBox
                showModal={isOfferMessageModalOpen}
                onClose={() => setIsOfferMessageModalOpen(false)}
                defaultHeader
                modalTitleTheme={`${cx('modalTitle')} ${dt([
                    'mb-2',
                    'text-left',
                    'text-black',
                    'text-2xlarge',
                ])}`}
                title={title}
                modalTheme={styles.customModalWrapper}
                automationId="footer-offer-message-popup"
            >
                <div
                    className={`${cx('customModalContent', 'customModalOfferMessage')} ${dt([
                        'font-normal',
                    ])}`}
                >
                    <div data-automation-id="footer-message-body">
                        <p
                            className={dt([
                                'text-base',
                                'pt-0',
                                'pr-0',
                                'leading-normal',
                                'text-black',
                            ])}
                        >
                            {Description}
                        </p>
                    </div>
                    <button
                        id="closeModalButton"
                        className={dt([
                            'text-center',
                            'mt-4',
                            'mb-0',
                            'mx-auto',
                            'block',
                            'sm:w-[200px]',
                            'bg-brand-red',
                            'shadow-elevation1',
                            'text-white',
                            'h-12',
                            'rounded-sm',
                            'sm:mt-[30px]',
                            'font-normal',
                            'text-base',
                        ])}
                        onClick={() => setIsOfferMessageModalOpen(false)}
                    >
                        {done}
                    </button>
                </div>
            </ModalBox>
        </>
    );
};

YodaSubscription.propTypes = {
    deviceType: PropTypes.object,
    expandedFooter: PropTypes.bool,
    offerMessageData: PropTypes.object,
    subscriptionStatus: PropTypes.object,
    featureFlags: PropTypes.object,
    actions: PropTypes.object,
};

export const mapStateToProps = (store) => ({
    subscriptionStatus: store.optin,
    deviceType: selectDeviceType(store),
    featureFlags: selectFeatureFlags(store),
});

export const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(Object.assign(analyticsActions, actions), dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(YodaSubscription);
